import { useState, useEffect, useCallback } from "react";
import { Zone, ZoneFormData } from "../types/zone";
import * as zoneService from "../services/zoneService";

interface UseZonesReturn {
    zones: Zone[];
    isLoading: boolean;
    error: string | null;
    fetchZones: () => Promise<void>;
    addZone: (data: ZoneFormData) => Promise<void>;
    editZone: (zoneId: string, data: ZoneFormData) => Promise<void>;
    removeZone: (zoneId: string) => Promise<void>;
}

export const useZones = (branchId: string | undefined): UseZonesReturn => {
    const [zones, setZones] = useState<Zone[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchZones = useCallback(async () => {
        if (!branchId) return;

        setIsLoading(true);
        setError(null);

        try {
            const data = await zoneService.getBranchZones(branchId);
            // Sort by fee (low to high) as per requirements
            const sorted = [...data].sort((a, b) => a.fee - b.fee);
            setZones(sorted);
        } catch (err: any) {
            setError(err.message || "Failed to fetch zones");
        } finally {
            setIsLoading(false);
        }
    }, [branchId]);

    const addZone = useCallback(async (data: ZoneFormData) => {
        if (!branchId) throw new Error("Branch ID is required");

        const newZone = await zoneService.createZone(branchId, data);
        setZones((prev) => [...prev, newZone].sort((a, b) => a.fee - b.fee));
    }, [branchId]);

    const editZone = useCallback(async (zoneId: string, data: ZoneFormData) => {
        if (!branchId) throw new Error("Branch ID is required");

        await zoneService.updateZone(branchId, zoneId, data);
        setZones((prev) =>
            prev
                .map((zone) =>
                    zone.id === zoneId ? { ...zone, ...data } : zone
                )
                .sort((a, b) => a.fee - b.fee)
        );
    }, [branchId]);

    const removeZone = useCallback(async (zoneId: string) => {
        if (!branchId) throw new Error("Branch ID is required");

        await zoneService.deleteZone(branchId, zoneId);
        setZones((prev) => prev.filter((zone) => zone.id !== zoneId));
    }, [branchId]);

    useEffect(() => {
        fetchZones();
    }, [fetchZones]);

    return {
        zones,
        isLoading,
        error,
        fetchZones,
        addZone,
        editZone,
        removeZone
    };
};
